/* eslint-disable camelcase */
import { beeTypes } from '@mailupinc/bee-plugin';
import {
  BeePluginContentDialogHandler,
  CustomRowConfiguration,
  IPluginRow,
} from '@mailupinc/bee-plugin/dist/types/bee';

import { MessageMedium } from 'constants/messageMediums';
import { DataWithGraphQLTypeName, GRAPHQL_TYPENAMES } from 'modules/graphql';
import { EditSavedRowDesignState } from 'modules/templateEditor/context/EditSavedRowDesignContext';
import { MutableRefObject } from 'react';
import { Product, Team } from 'teams';
import { NoOp } from 'utils/noop';
import { ReplaceReturnType } from 'utils/tsHelpers';

/**
 * Bee Plugin configuration
 * @see https://docs.beefree.io/configuration-parameters
 */
export interface BeeConfig extends beeTypes.IBeeConfig {}

export type AuthToken = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  userName: string;
  'as:client_id': string;
  'as:region': string;
  '.issued': string;
  '.expires': string;
};

export type TrackErrorConfig = {
  token?: AuthToken;
  error?: Error;
  errorMessage: string;
  errorName?: string;
  productName?: Product;
  teamName?: Team;
};

export enum AnchorTarget {
  blank = '_blank',
  self = '_self',
  top = '_top',
}

export enum ContentModuleType {
  html = 'html',
  mixed = 'mixed',
  paragraph = 'paragraph',
  image = 'image',
}

export interface ContentModule {
  type: ContentModuleType;
}

export interface MixedContentModule extends ContentModule {
  type: ContentModuleType.mixed;
  value: ContentModule[];
}

export interface HtmlContentModule extends ContentModule {
  type: ContentModuleType.html;
  value: {
    html: string;
  };
}

export interface ParagraphContentModule extends ContentModule {
  type: ContentModuleType.paragraph;
  value: {
    html: string;
  };
}

// https://docs.beefree.io/beefree-sdk/data-structures/simple-schema/image-schema#structure-definition
// dynamicSrc, padding, locked, & customFields were ommitted: they do not currently fit our use case
export interface ImageContentModule extends ContentModule {
  type: ContentModuleType.image;
  value: {
    alt?: string;
    href?: string;
    src?: string;
    target?: AnchorTarget;
  };
}

export type AddOn = {
  id: string;
  enabled: boolean;
};

export type ContentDialogHandler = (
  resolve: (obj: Record<string, any>) => void,
  reject: NoOp,
  args: Record<string, any>,
) => void;

export type ContentDialogId = string;

export type ContentDialogHandlerMapping = {
  [contentDialogId: ContentDialogId]: ContentDialogHandler;
};

export enum DragAndDropEditorError {
  DRAG_AND_DROP_EDITOR_UNKNOWN_ERROR = 'DRAG_AND_DROP_EDITOR_UNKNOWN_ERROR',
  DRAG_AND_DROP_EDITOR_START_ERROR = 'DRAG_AND_DROP_EDITOR_START_ERROR',
  DRAG_AND_DROP_EDITOR_INITIALIZE_ERROR = 'DRAG_AND_DROP_EDITOR_INITIALIZE_ERROR',
  DRAG_AND_DROP_EDITOR_INVALID_TOKEN_ERROR = 'DRAG_AND_DROP_EDITOR_INVALID_TOKEN_ERROR',
  DRAG_AND_DROP_EDITOR_SINGLE_ROW_START_ERROR = 'DRAG_AND_DROP_EDITOR_SINGLE_ROW_START_ERROR',
  DRAG_AND_DROP_EDITOR_SINGLE_ROW_INITIALIZE_ERROR = 'DRAG_AND_DROP_EDITOR_SINGLE_ROW_INITIALIZE_ERROR',
}

export type OnGetContentRef = (...args: any) => void;
export type GetContentCallback = (args: { html: string }) => void;

export type SavedRowsConfig = {
  saveRows: BeeConfig['saveRows'];
  rowsConfiguration: BeeConfig['rowsConfiguration'];
  onSaveRow?: BeeConfig['onSaveRow'];
};

type User = {
  id: string;
  fullName: string;
  avatarUrl?: string;
};

export type SavedRowPrimaryContent = {
  id: number;
  html: string;
  beeBuilderMetadata: string;
  creatorUser: User;
  createdAt: string;
};

export type CategoryDefinition = {
  id: number;
  name: string;
  creatorUser: User;
  updatedByUser: User;
  createdAt: string;
  updatedAt: string;
};

export type SavedRow = {
  id: number;
  name: string;
  messageMedium: MessageMedium;
  category: CategoryDefinition;
  primaryContent?: SavedRowPrimaryContent;
  createdAt: string;
  updatedAt: string;
  creatorUser: User;
  updatedByUser: User;
};

/*
 * We are using custom type due to the BEE plugin bug from their old version.
 * After updating BEE version, we can remove this.
 * */
export type Hooks = {
  getRows: {
    handler: BeePluginContentDialogHandler<
      IPluginRow[],
      undefined,
      { handle: string; value: string }
    >;
  };
};

export type MessageCategory = {
  id: number;
  name: string;
  projectId: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  rowsCount: number;
};

export type Failure = {
  errorMsg: string;
};

export type BeeSavedRowMutationFailure =
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_CREATE_FIELD_NOT_PROVIDED, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_CREATE_FIELD_TOO_LONG_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_FIELD_INVALID_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_ROW_NAME_ALREADY_EXISTS_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_SAVED_ROW_CREATION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_SAVED_ROW_DELETION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_ROW_UPDATE_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.BEE_SAVED_ROW_MUTATION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_CREATION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_UPDATE_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_DELETION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MANAGE_MESSAGE_CATEGORIES_FAILURE, Failure>
  | DataWithGraphQLTypeName<
      GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_MAXIMUM_COUNT_EXCEEDED_FAILURE,
      Failure
    >
  | DataWithGraphQLTypeName<
      GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_TOO_LONG_FAILURE,
      { maximumLength: number }
    >
  | null;

export type CreateSavedRowResponse = {
  createBeeRow: {
    beeRowMetadataId: number | null;
    beeRowName: string | null;
    failure: BeeSavedRowMutationFailure;
  };
};

export type UpdateSavedRowResponse = {
  updateBeeRow: {
    beeRowMetadataId: number | null;
    beeRowName: string | null;
    failure: BeeSavedRowMutationFailure;
  };
};

export type UpsertSavedRowVariables = {
  rowName: string;
  messageMedium: MessageMedium;
  html: string;
  beeMetadata: string;
  categoryId?: number;
  categoryName?: string;
};

export type DeleteSavedRowResponse = {
  deleteBeeRow: {
    beeRowName: string | null;
    beeRowMetadataId: number | null;
    failure: BeeSavedRowMutationFailure;
  };
};

export type DeleteSavedRowVariables = {
  rowId: number;
};

export type OnEditSavedRow = ReplaceReturnType<
  BeePluginContentDialogHandler<boolean, undefined, unknown>
>;
export type OnCreateSavedRow = ReplaceReturnType<
  BeePluginContentDialogHandler<Record<string, unknown>, undefined, unknown>
>;
export type OnDeleteSavedRow = ReplaceReturnType<
  BeePluginContentDialogHandler<boolean, undefined, unknown>
>;
export type OnManageCustomCategories = ReplaceReturnType<
  BeePluginContentDialogHandler<CustomRowConfiguration>
>;

export type DragAndDropEditorProps = {
  /**
   * used to determine when to reset the editor (new locale may have the same initValue)
   */
  contentLocale?: string;
  /**
   * used to determine when to reset the editor (we may want to re-init the editor even with no change to locale or initValue)
   */
  initTs?: number;
  /**
   * beeBuilderMetadata: this is not a controlled component, so we need to be aware of conditions (like switching locale) which require a re-init
   */
  initValue?: string;
  /**
   * The medium of the template being edited
   */
  messageMedium: MessageMedium;
  /**
   * event-triggered function to update both values and follow with callback
   */
  onUpdateValues: (json: string, html: string, updateCallback: GetContentCallback) => void;
  onChange: (json: string) => void;
  onGetContentRef: MutableRefObject<OnGetContentRef | undefined>;
  onEditorLoad: () => void;
  editSavedRowDesign: EditSavedRowDesignState;
  /**
   * Whether the template has unsaved changes
   */
  hasUnsavedTemplateChanges: boolean;
};
